/** Web-side lookups over every module: contracts plus their UI. */
import type { LucideIcon } from "lucide-react";
import { buildRegistry, type EventTypeDef, type SubjectKind } from "../contract";
import { contracts } from "../modules/contracts";
import { webParts } from "../modules/web";
import type { AnimalAction, AnimalSection, EntitySection } from "./module";
import { CORE_ICONS } from "./ui/icons";

export const registry = buildRegistry(contracts);
export const webModules = webParts;

export const icons: Record<string, LucideIcon> = Object.assign({}, CORE_ICONS, ...webParts.map((m) => m.icons ?? {}));
export const iconFor = (name: string | undefined): LucideIcon => (name && icons[name]) || CORE_ICONS.dot;

export const animalSections: AnimalSection[] = webParts.flatMap((m) => m.animalSections ?? []).sort((a, b) => a.order - b.order);
export const animalActions: AnimalAction[] = webParts.flatMap((m) => m.animalActions ?? []).sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

export function eventView(type: string) {
  for (const m of webParts) if (m.events?.[type]) return m.events[type];
  return undefined;
}

export function workspaceComponent(id: string) {
  for (const m of webParts) if (m.workspaces?.[id]) return m.workspaces[id];
  return undefined;
}

export function ranchSectionComponent(id: string) {
  for (const m of webParts) if (m.ranchSections?.[id]) return m.ranchSections[id];
  return undefined;
}

export function jobComponent(type: string) {
  for (const m of webParts) if (m.jobs?.[type]) return m.jobs[type];
  return undefined;
}

export function entitySections(kind: "place" | "group" | "contact" | "asset" | "stock"): EntitySection<Record<string, any>>[] {
  const key = `${kind}Sections` as const;
  return webParts.flatMap((m) => m[key] ?? []).sort((a, b) => a.order - b.order);
}

/** Event types a person can add for a subject kind, for "Add a record" menus. */
export function eventTypesFor(kind: SubjectKind): EventTypeDef[] {
  return [...registry.events.values()].filter((e) => !e.hidden && e.subjects.some((s) => s.kind === kind));
}
